import { getCache } from './indexeddb-cache';
import { readAdminCredentialRevision } from './storage/admin-credential-revision';
import { loadAnthropicMetadataCache, METADATA_CACHE_TTL_MS } from './anthropic-metadata-cache';
import type { CachedUsageDetails } from './openai-api/types';

/** Read current, complete key inventories without starting provider requests. */
export async function loadMappingApiKeys(): Promise<Array<{ id: string; name: string }>> {
  const revision = readAdminCredentialRevision();
  const entries = await getCache().list<CachedUsageDetails>('openai', 'organization_usage_details', true);
  let latest: typeof entries[number] | undefined;
  for (const entry of entries) {
    if (entry.params?.credentialRevision !== revision || entry.data?.partialErrors?.length
      || !Array.isArray(entry.data?.apiKeyUsage)) continue;
    if (!latest || entry.timestamp > latest.timestamp) latest = entry;
  }
  const metadata = loadAnthropicMetadataCache(revision);
  const freshMetadata = metadata && Date.now() - metadata.fetchedAt < METADATA_CACHE_TTL_MS ? metadata : null;
  if (revision !== readAdminCredentialRevision()) throw new Error('Credentials changed; reload API key candidates.');
  if (!latest && !freshMetadata) throw new Error('No current API key inventory. Load the Usage Dashboard, then refresh candidates.');
  const keys = new Map<string, { id: string; name: string }>();
  for (const key of latest?.data.apiKeyUsage ?? []) {
    if (typeof key.id === 'string') keys.set(key.id, { id: key.id, name: key.name || key.id });
  }
  for (const key of Object.values(freshMetadata?.data ?? {})) {
    keys.set(key.id, { id: key.id, name: key.name || key.id });
  }
  return [...keys.values()];
}
